import { useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import type { Order, OrderHistory, OrderItem, User, Location } from '../types';

// Options for fetching a single order with related data
export interface UseOrderOptions {
  /** Include order items with product details (default: true) */
  includeItems?: boolean;
  /** Include customer information (default: true) */
  includeCustomer?: boolean;
  /** Include staff information (default: false) */
  includeStaff?: boolean;
  /** Include order history timeline (default: true) */
  includeHistory?: boolean;
  /** Include reseller information for B2B orders (default: false) */
  includeReseller?: boolean;
  /** Include location information (default: false) */
  includeLocation?: boolean;
  /** Enable the query (default: true when id is provided) */
  enabled?: boolean;
}

// Extended order type with guaranteed included relations
export interface OrderWithDetails extends Order {
  items: OrderItem[];
  history?: OrderHistory[];
  customer?: User;
  staff?: User;
  location?: Location;
}

// Default options for single order queries - always include items and history
const DEFAULT_OPTIONS: UseOrderOptions = {
  includeItems: true,
  includeCustomer: true,
  includeStaff: false,
  includeHistory: true,
  includeReseller: false,
  includeLocation: false,
};

// API functions for single order operations
export const orderApi = {
  /**
   * Fetch a single order by ID with optional related data
   */
  getOrder: async (id: string, options: UseOrderOptions = {}): Promise<OrderWithDetails> => {
    const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
    const params: Record<string, boolean> = {};

    if (mergedOptions.includeItems) params.includeItems = true;
    if (mergedOptions.includeCustomer) params.includeCustomer = true;
    if (mergedOptions.includeStaff) params.includeStaff = true;
    if (mergedOptions.includeHistory) params.includeHistory = true;
    if (mergedOptions.includeReseller) params.includeReseller = true;
    if (mergedOptions.includeLocation) params.includeLocation = true;

    const response = await axiosInstance.get<OrderWithDetails>(`/orders/${id}`, { params });
    return response.data;
  },

  /**
   * Fetch order history separately (for lazy loading or refreshing just history)
   */
  getOrderHistory: async (orderId: string): Promise<OrderHistory[]> => {
    const response = await axiosInstance.get<OrderHistory[]>(`/orders/${orderId}/history`);
    return response.data;
  },
};

/**
 * Hook for fetching a single order with full details and history.
 *
 * By default, includes items, customer, and history for the order detail view.
 *
 * @param id - Order UUID
 * @param options - Options for including related data and query control
 * @returns TanStack Query result with order data including history
 *
 * @example
 * ```tsx
 * // Basic usage - includes items, customer, and history by default
 * const { data: order, isLoading, error } = useOrder('order-uuid');
 *
 * // With additional options
 * const { data: order } = useOrder('order-uuid', {
 *   includeStaff: true,
 *   includeLocation: true,
 *   includeReseller: true, // For B2B orders
 * });
 *
 * // Access history
 * order?.history?.forEach(entry => {
 *   console.log(entry.action, entry.createdAt);
 * });
 * ```
 */
export const useOrder = (id: string | undefined | null, options: UseOrderOptions = {}) => {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
  const isEnabled = options.enabled !== undefined ? options.enabled && !!id : !!id;

  return useQuery({
    queryKey: ['order', id, mergedOptions],
    queryFn: () => orderApi.getOrder(id!, mergedOptions),
    enabled: isEnabled,
    staleTime: 1000 * 60 * 2, // Consider data fresh for 2 minutes
  });
};

/**
 * Hook for fetching order history separately.
 *
 * Useful for refreshing just the history timeline without refetching the entire order.
 *
 * @param orderId - Order UUID
 * @param options - Query options
 * @returns TanStack Query result with order history entries
 *
 * @example
 * ```tsx
 * const { data: history, refetch } = useOrderHistory('order-uuid');
 *
 * // Manually refresh history after an action
 * const handleStatusUpdate = async () => {
 *   await updateStatus();
 *   refetch();
 * };
 * ```
 */
export const useOrderHistory = (orderId: string | undefined | null, options?: { enabled?: boolean }) => {
  const isEnabled = options?.enabled !== undefined ? options.enabled && !!orderId : !!orderId;

  return useQuery({
    queryKey: ['order', orderId, 'history'],
    queryFn: () => orderApi.getOrderHistory(orderId!),
    enabled: isEnabled,
    staleTime: 1000 * 30, // Consider history fresh for 30 seconds
  });
};

/**
 * Hook to get utility functions for order cache management.
 *
 * @returns Object with cache invalidation and prefetching utilities
 *
 * @example
 * ```tsx
 * const { invalidateOrder, prefetchOrder } = useOrderUtils();
 *
 * // Invalidate cache after mutation
 * const handleUpdate = async (orderId: string) => {
 *   await updateOrder(orderId);
 *   invalidateOrder(orderId);
 * };
 *
 * // Prefetch on hover
 * <OrderRow
 *   onMouseEnter={() => prefetchOrder(order.id)}
 * />
 * ```
 */
export const useOrderUtils = () => {
  const queryClient = useQueryClient();

  return {
    /**
     * Invalidate order cache to trigger refetch
     */
    invalidateOrder: (orderId: string) => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
    },

    /**
     * Invalidate only the history cache for an order
     */
    invalidateOrderHistory: (orderId: string) => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId, 'history'] });
    },

    /**
     * Prefetch order data (useful for hover states)
     */
    prefetchOrder: (orderId: string, options: UseOrderOptions = {}) => {
      const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
      queryClient.prefetchQuery({
        queryKey: ['order', orderId, mergedOptions],
        queryFn: () => orderApi.getOrder(orderId, mergedOptions),
        staleTime: 1000 * 60 * 2,
      });
    },

    /**
     * Get cached order data without triggering a fetch
     */
    getOrderFromCache: (orderId: string): OrderWithDetails | undefined => {
      return queryClient.getQueryData(['order', orderId]);
    },

    /**
     * Update order data in cache (for optimistic updates)
     */
    setOrderInCache: (orderId: string, data: OrderWithDetails | ((old: OrderWithDetails | undefined) => OrderWithDetails)) => {
      queryClient.setQueryData(['order', orderId], data);
    },
  };
};

// Re-export types for convenience
export type { OrderHistory, OrderItem } from '../types';
